import React, { useState, useEffect } from "react"
import { BookMarked, Trophy, ClipboardCheck, Award } from "lucide-react"
import { computeProofOfLearning } from "../../../utils/proofOfLearningHelpers"
import MinimalStepsCard from "../../../components/ui/minimal-steps-card"

const RANGE_OPTIONS = [
  { key: "1d", label: "Today" },
  { key: "7d", label: "7 Days" },
  { key: "30d", label: "30 Days" },
  { key: "90d", label: "90 Days" },
]

const RANGE_SUBTITLES = { "1d": "Today", "7d": "Last 7 Days", "30d": "Last 30 Days", "90d": "Last 90 Days" }

const ProofOfLearningSection = ({ orgId }) => {
  const [range, setRange] = useState("30d")
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    computeProofOfLearning(orgId, range).then((result) => {
      setData(result)
      setLoading(false)
    })
  }, [orgId, range])

  if (loading || !data) {
    return (
      <div className="mb-6">
        <div className="h-6 w-40 bg-muted rounded animate-pulse mb-4" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 })?.map((_, i) => (
            <div key={i} className="h-64 rounded-2xl bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  const subtitle = RANGE_SUBTITLES?.[range]

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-foreground mb-1">Proof of Learning</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Every course, assessment, and credential leaves a verifiable record — this is that record, at a glance.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MinimalStepsCard
          title="Learning Records"
          subtitle={subtitle}
          totalSteps={data?.learningRecords?.total}
          stepsUnit="records"
          icon={<BookMarked className="h-5 w-5 text-muted-foreground" />}
          data={data?.learningRecords?.data}
          rangeOptions={RANGE_OPTIONS}
          selectedRange={range}
          onRangeChange={setRange}
        />
        <MinimalStepsCard
          title="Achievements Recorded"
          subtitle={subtitle}
          totalSteps={data?.achievementsRecorded?.total}
          stepsUnit="achievements"
          icon={<Trophy className="h-5 w-5 text-muted-foreground" />}
          data={data?.achievementsRecorded?.data}
          rangeOptions={RANGE_OPTIONS}
          selectedRange={range}
          onRangeChange={setRange}
        />
        <MinimalStepsCard
          title="Assessment Evidence"
          subtitle={subtitle}
          totalSteps={data?.assessmentEvidence?.total}
          stepsUnit="attempts"
          icon={<ClipboardCheck className="h-5 w-5 text-muted-foreground" />}
          data={data?.assessmentEvidence?.data}
          rangeOptions={RANGE_OPTIONS}
          selectedRange={range}
          onRangeChange={setRange}
        />
        <MinimalStepsCard
          title="Credential Evidence"
          subtitle={subtitle}
          totalSteps={data?.credentialEvidence?.total}
          stepsUnit="credentials"
          icon={<Award className="h-5 w-5 text-muted-foreground" />}
          data={data?.credentialEvidence?.data}
          rangeOptions={RANGE_OPTIONS}
          selectedRange={range}
          onRangeChange={setRange}
        />
      </div>
    </div>
  )
}

export default ProofOfLearningSection